import React, { useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CssBaseline from '@mui/material/CssBaseline';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import { withStyles } from "@material-ui/core/styles";

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

// Custom Radio Color
const CustomRadio = withStyles({
  root: { color: "gray", "&$checked": { color: "black" } },
  checked: {}
})(props => <Radio color="default" {...props} />);

const BlackLabel = withStyles({
  label: { color: "black" }
})(FormControlLabel);

const drawerWidth = 240;

// Layout styling
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    ...(open && {
      marginRight: 0,
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen
      })
    })
  })
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open"
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginRight: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  ...theme.mixins.toolbar,
  padding: theme.spacing(0, 1),
  justifyContent: "flex-start"
}));

export default function Sidebar() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("dev");

  // dialogs
  const [progressDialog, setProgressDialog] = useState(false);
  const [resultDialog, setResultDialog] = useState({
    open: false,
    message: "",
    success: false
  });

  const handleEnvChange = (event) => {
    window.environment = event.target.value;
    localStorage.setItem("environment", event.target.value);
    window.location.reload();
  };

  useEffect(() => {
    const savedEnv = localStorage.getItem("environment") || "dev";
    window.environment = savedEnv;
    setSelectedOption(savedEnv);
  }, []);

  const openDrawer = () => setOpen(true);
  const closeDrawer = () => setOpen(false);

  const handleRestartShellhub = async () => {
    setProgressDialog(true);

    try {
      const res = await fetch("https://dev.phyfarm.com/api/mgmt/shellhub/restart", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}"
      });

      setProgressDialog(false);

      if (res.status === 200) {
        setResultDialog({
          open: true,
          success: true,
          message: "Shellhub restarted successfully!"
        });
      } else {
        setResultDialog({
          open: true,
          success: false,
          message: "Shellhub restart failed."
        });
      }
    } catch (e) {
      setProgressDialog(false);
      setResultDialog({
        open: true,
        success: false,
        message: "Shellhub restart failed."
      });
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* TOP BAR */}
      <AppBar position="fixed" open={open}>
        <Toolbar>

          <FormControl sx={{ flexGrow: 1, marginLeft: 10 }}>
            <RadioGroup row>
              <BlackLabel value="dev" checked={selectedOption === "dev"} onChange={handleEnvChange} control={<CustomRadio />} label="Dev" />
              <BlackLabel value="prod" checked={selectedOption === "prod"} onChange={handleEnvChange} control={<CustomRadio />} label="Prod" />
            </RadioGroup>
          </FormControl>

          <Button variant="contained" style={{ background: "red", marginRight: 20 }} onClick={handleRestartShellhub}>
            Restart Shellhub
          </Button>

          <IconButton color="inherit" edge="end" onClick={openDrawer} sx={{ ...(open && { display: "none" }) }}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* DRAWER */}
      <Drawer
        anchor="right"
        open={open}
        variant="persistent"
        sx={{
          width: drawerWidth,
          '& .MuiDrawer-paper': { width: drawerWidth }
        }}
      >
        <DrawerHeader>
          <IconButton onClick={closeDrawer}>
            {theme.direction === "rtl" ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>

        <Divider />

        <List><Link style={linkStyle} to="/environment">Environment</Link></List>
        <List><Link style={linkStyle} to="/devices">Devices</Link></List>
        <List><Link style={linkStyle} to="/farms">Farms</Link></List>
        <List><Link style={linkStyle} to="/payments">Payments</Link></List>
        <List><Link style={linkStyle} to="/listenvs">List Envs</Link></List>
        <List><Link style={linkStyle} to="/userManagement">User Management</Link></List>
        <List><Link style={linkStyle} to="/phySenseConfig">PhySense Config Builder</Link></List>
        <List><Link style={linkStyle} to="/enquiry">Enquiry</Link></List>
        <List><Link style={linkStyle} to="/SystemStatus">System Status</Link></List>

        <Divider />
      </Drawer>

      {/* LOADING DIALOG */}
      <Dialog open={progressDialog} disableEscapeKeyDown>
        <DialogContent sx={{ textAlign: "center", padding: 3 }}>
          <CircularProgress />
          <Typography sx={{ marginTop: 2 }}>Please wait… restarting Shellhub</Typography>
        </DialogContent>
      </Dialog>

      {/* RESULT DIALOG */}
      <Dialog open={resultDialog.open} onClose={() => setResultDialog({ ...resultDialog, open: false })}>
        <DialogTitle style={{ color: resultDialog.success ? "green" : "red" }}>
          {resultDialog.success ? "Success" : "Failed"}
        </DialogTitle>
        <DialogContent>
          <Typography>{resultDialog.message}</Typography>
        </DialogContent>
      </Dialog>

    </Box>
  );
}

const linkStyle = {
  color: "black",
  textDecoration: "none",
  marginLeft: 10,
  marginTop: 15,
  float: "left"
};
